import type { ResourceOccupancyEntry } from '@/shared/api';
import { formatMoney } from '@/shared/lib/format';

function clock(iso: string, timezone: string): string {
  return new Date(iso).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  });
}

/**
 * Was mit dem Geld einer bezahlten Reservierung ist — als Satz, nicht nur als Farbe. Eingegangenes
 * Geld, das zurückgeht, überdeckt jeden anderen Zustand, weil der Betrieb genau das wissen muss.
 */
export function paymentText(entry: ResourceOccupancyEntry, timezone: string): string | null {
  const payment = entry.payment;
  if (!payment) return null;
  const amount = formatMoney(payment.amount, payment.currency);
  if (payment.refundState === 'Pending') return `${amount} – Erstattung läuft`;
  if (payment.refundState === 'Refunded') return `${amount} – erstattet`;
  if (payment.refundState === 'ReviewRequired') return `${amount} – Erstattung in Klärung`;
  switch (payment.checkoutStatus) {
    case 'Held':
      return `${amount} – Zahlung offen bis ${clock(payment.holdExpiresAt, timezone)} Uhr`;
    case 'Expired':
      return `${amount} – Frist abgelaufen, nicht bezahlt`;
    case 'Aborted':
      return payment.endReason === 'Guest'
        ? `${amount} – vom Gast abgebrochen, nicht bezahlt`
        : `${amount} – beendet, nicht bezahlt`;
    case 'Completed':
      return entry.status === 'Cancelled'
        ? `${amount} bezahlt – aufgehoben ohne Erstattung`
        : `${amount} bezahlt`;
    default:
      // Ein neuer Zustand des Servers bleibt sichtbar, statt als „bezahlt" durchzugehen.
      return `${amount} – Zahlungsstand unbekannt (${String(payment.checkoutStatus)})`;
  }
}

export interface CancelPrompt {
  title: string;
  description: string;
  confirmLabel: string;
  confirmPaid: boolean;
}

/**
 * Was die Aufhebung bewirkt. Eine bezahlte Reservierung gibt die Zeit frei und erstattet nichts
 * (PO-Entscheid) — der Dialog sagt es, und erst seine Bestätigung schickt `confirmPaid`.
 */
export function cancelPrompt(entry: ResourceOccupancyEntry): CancelPrompt {
  if (entry.kind === 'Block') {
    return {
      title: 'Sperre aufheben?',
      description: 'Die Zeit wird sofort wieder buchbar.',
      confirmLabel: 'Aufheben',
      confirmPaid: false,
    };
  }
  if (entry.status === 'Held') {
    return {
      title: 'Gehaltene Auswahl beenden?',
      description:
        'Der Gast bezahlt gerade. Die Zeit wird frei und die Zahlung abgebrochen; ist sie schon eingegangen, wird sie voll erstattet.',
      confirmLabel: 'Beenden',
      confirmPaid: false,
    };
  }
  if (entry.payment?.checkoutStatus === 'Completed') {
    return {
      title: 'Bezahlte Reservierung aufheben?',
      description: `Der Gast hat ${formatMoney(entry.payment.amount, entry.payment.currency)} bezahlt. Die Zeit wird frei, das Geld wird nicht erstattet und der Gast bekommt keine Nachricht.`,
      confirmLabel: 'Ohne Erstattung aufheben',
      confirmPaid: true,
    };
  }
  return {
    title: 'Reservierung aufheben?',
    description:
      'Die kostenlose Reservierung wird aufgehoben und die Zeit wieder frei. Der Gast bekommt keine Nachricht.',
    confirmLabel: 'Aufheben',
    confirmPaid: false,
  };
}
